import { EditProjectIqWorkspaceDialog } from "apps/project-iq/components/EditProjectIqWorkspaceDialog/EditProjectIqWorkspaceDialog";
import {
  SearchIcon,
  PlusIcon,
} from "apps/project-iq/components/icons/ProjectIqIcons";
import { WorkspaceCard } from "apps/project-iq/components/WorkspaceCard/WorkspaceCard";
import { Skeleton, SkeletonItem } from "@fluentui/react-components";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  selectAvailableProjectIqWorkspaces,
  selectIsProjectIqWorkspacesLoading,
} from "redux/projectIqWorkspacesSlice";
import { useProjectIqSharedButtonStyles } from "apps/project-iq/theme/projectIqSharedStyles";
import { useProjectIqWorkspacesPageStyles } from "./ProjectIqWorkspacesPage.styles";

const SKELETON_CARD_COUNT = 6;

const WorkspaceCardSkeleton = (): JSX.Element => {
  const styles = useProjectIqWorkspacesPageStyles();
  return (
    <div className={styles.skeletonCard}>
      <Skeleton>
        <div className={styles.skeletonCardContent}>
          <SkeletonItem animation="wave" className={styles.skeletonCardName} />
          <SkeletonItem
            animation="wave"
            className={styles.skeletonCardSubname}
          />
        </div>
      </Skeleton>
    </div>
  );
};

interface IProjectIqWorkspacesPageProps {
  pinnedIdentifiers: string[];
  onTogglePin: (id: string, e: React.MouseEvent) => void;
}

export const ProjectIqWorkspacesPage = ({
  pinnedIdentifiers,
  onTogglePin,
}: IProjectIqWorkspacesPageProps): JSX.Element => {
  const styles = useProjectIqWorkspacesPageStyles();
  const buttonStyles = useProjectIqSharedButtonStyles();
  const navigate = useNavigate();
  const workspaces = useSelector(selectAvailableProjectIqWorkspaces);
  const isWorkspacesLoading = useSelector(selectIsProjectIqWorkspacesLoading);

  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const filtered = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return workspaces;

    return workspaces.filter(
      (w) =>
        w.name.toLowerCase().includes(query) ||
        (w.clientName ?? "").toLowerCase().includes(query) ||
        (w.code ?? "").toLowerCase().includes(query),
    );
  }, [workspaces, searchQuery]);

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <span className={styles.topBarTitle}>Project IQ</span>
      </div>
      <div className={styles.content}>
        <h1 className={styles.pageTitle}>Your Workspaces</h1>

        <div className={styles.searchRow}>
          <div className={styles.searchBox}>
            <SearchIcon />
            <input
              className={styles.searchInput}
              placeholder="Search names, clients, codes…"
              aria-label="Search workspaces"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            className={buttonStyles.tealButton}
            onClick={() => setIsDialogOpen(true)}
            aria-label="New workspace"
          >
            <PlusIcon />
            New workspace
          </button>
        </div>

        {isWorkspacesLoading ? (
          <div className={styles.grid}>
            {Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
              <WorkspaceCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <>
            {filtered.length === 0 ? (
              <div className={styles.emptyState}>No workspaces found.</div>
            ) : (
              <div className={styles.grid}>
                {filtered.map((w) => (
                  <WorkspaceCard
                    key={w.id}
                    workspace={w}
                    isPinned={pinnedIdentifiers.includes(w.id)}
                    onClick={() => navigate(`/workspace/${w.id}`)}
                    onTogglePin={(e) => onTogglePin(w.id, e)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {isDialogOpen && (
        <EditProjectIqWorkspaceDialog
          isOpen={isDialogOpen}
          workspace={null}
          onDismiss={(isSuccess, createdWorkspace) => {
            setIsDialogOpen(false);
            if (isSuccess && createdWorkspace?.id) {
              navigate(`/workspace/${createdWorkspace.id}`);
            }
          }}
        />
      )}
    </div>
  );
};
